import { useEffect, useMemo, useReducer } from 'react';
import { Check, Pencil, RefreshCw, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Product } from '@/features/product-scan/product-scan.schema';

import { formatCompactMoney, tenureLabel } from '../customer-derived';
import { eligibleTargets } from '../customer-eligibility';
import { useToast } from '../toast-context';
import {
  CC_STEPS,
  resolveTargets,
  targetSnapshot,
  canContinue,
  ccReducer,
  gatingHint,
  initCcState,
  type WizardInit,
} from './campaign-state';
import { detectTuneStyle, TUNE_PRESETS } from './message-templates';

interface CampaignWizardProps {
  open: boolean;
  init: WizardInit;
  customers: Customer[];
  products: Product[];
  onClose: () => void;
  onExport: (state: {
    targetIds: string[];
    product: Product | null;
    campaignName: string;
    emailSubject: string;
    emailBody: string;
    talkTrack: string;
    exportFormat: 'csv' | 'copy';
    previewTab: 'talk' | 'email';
  }) => void;
}

/**
 * The 3-step New Campaign wizard (SCRUM-209, restructured by SCRUM-497):
 * Product → Messaging → Preview. The Audience step was removed with the
 * audience feature (decision 2026-07-10 — documented deviation from the v7
 * prototype): the target set is either the contextual seed the wizard was
 * opened with (referral / drawer / ledger selection) or, when unseeded,
 * product-first auto-targeting from eligibility rules (SCRUM-500). Step
 * gating, product picker/detail, template generation + Tune, Save as Draft;
 * the final step's Continue triggers export.
 */
export function CampaignWizard({
  open,
  init,
  customers,
  products,
  onClose,
  onExport,
}: CampaignWizardProps) {
  const { showToast } = useToast();
  const [state, dispatch] = useReducer(ccReducer, init, initCcState);

  const product = useMemo(
    () => products.find((p) => p.id === state.productId) ?? null,
    [products, state.productId],
  );
  // Target resolution (SCRUM-500): contextual seed when present, otherwise
  // auto-computed from the selected product's eligibility rules.
  const targets = useMemo(
    () => resolveTargets(state.targetIds, product, customers),
    [state.targetIds, product, customers],
  );
  // Candidate pool for the product cards' "N of M eligible" counts: the seed
  // in contextual mode, the whole book in auto-targeting mode.
  const candidates = useMemo(
    () => (state.targetIds ? targets : customers),
    [state.targetIds, targets, customers],
  );

  // Seed name + drafts whenever the chosen product changes (parallels Ryan's
  // open flow). The reducer handles the "only name when empty" guard, so this
  // effect reads no state and depends solely on the product.
  useEffect(() => {
    dispatch({ type: 'seedForProduct', product });
  }, [product]);

  function handleContinue() {
    if (state.step === CC_STEPS.length - 1) {
      onExport({
        targetIds: targets.map((c) => c.id),
        product,
        campaignName: state.campaignName,
        emailSubject: state.emailSubject,
        emailBody: state.emailBody,
        talkTrack: state.talkTrack,
        exportFormat: state.exportFormat,
        previewTab: state.previewTab,
      });
      return;
    }
    dispatch({ type: 'next' });
  }

  function applyTune(tab: 'talk' | 'email', freeform: string) {
    if (!product) return;
    const combined = [...state.tuneChips[tab], freeform].filter(Boolean).join(', ');
    const style = detectTuneStyle(combined);
    dispatch({ type: 'setGenerating', tab, value: true });
    dispatch({ type: 'applyTune', tab, product, style });
    // Simulate the ~400ms AI generation feel.
    setTimeout(() => {
      dispatch({ type: 'setGenerating', tab, value: false });
    }, 400);
  }

  const blocked = !canContinue(state, targets.length);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      {/* Responsive width (.claude/rules/frontend.md "Frontend Must Be Responsive"): target 820px on
          desktop, never exceed the viewport, and never narrower than the stepper needs.
          The explicit sm:max-w override is required because DialogContent's base class
          ships sm:max-w-sm, which a plain max-w-[820px] would not beat. */}
      <DialogContent className="flex max-h-[85vh] w-[min(820px,calc(100vw-2rem))] max-w-[820px] min-w-[min(640px,calc(100vw-2rem))] flex-col gap-0 p-0 sm:max-w-[820px]">
        <DialogHeader className="border-border-1 border-b px-6 py-4">
          <DialogTitle className="font-display text-lg">New Campaign</DialogTitle>
        </DialogHeader>

        {/* Stepper — wraps rather than clips on tight widths (.claude/rules/frontend.md responsiveness). */}
        <div className="border-border-1 flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b px-6 py-3">
          {CC_STEPS.map((s, i) => {
            const isActive = i === state.step;
            const isComplete = i < state.step;
            return (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    isActive
                      ? 'bg-brand-primary text-white'
                      : isComplete
                        ? 'bg-success text-white'
                        : 'bg-bg-3 text-text-3'
                  }`}
                >
                  {isComplete ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span
                  className={`text-sm whitespace-nowrap ${isActive ? 'text-text-0 font-medium' : 'text-text-3'}`}
                >
                  {s.label}
                </span>
                {i < CC_STEPS.length - 1 && (
                  <span className="bg-border-1 mx-1 hidden h-px w-6 sm:block" />
                )}
              </div>
            );
          })}
        </div>

        {/* Step body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {state.step === 0 && (
            <ProductStep
              product={product}
              products={products}
              candidates={candidates}
              targets={targets}
              onSelect={(id) => {
                dispatch({ type: 'selectProduct', productId: id });
              }}
              onClear={() => {
                dispatch({ type: 'clearProduct' });
              }}
            />
          )}
          {state.step === 1 && (
            <MessagingStep
              state={state}
              hasProduct={product !== null}
              onSetName={(name) => {
                dispatch({ type: 'setName', name });
              }}
              onToggleNameEdit={() => {
                dispatch({ type: 'toggleNameEditing' });
              }}
              onSetTab={(tab) => {
                dispatch({ type: 'setPreviewTab', tab });
              }}
              onRegenerate={(tab) => {
                if (product) dispatch({ type: 'regenerate', tab, product });
              }}
              onAddChip={(tab, chip) => {
                dispatch({ type: 'addChip', tab, chip });
              }}
              onRemoveChip={(tab, index) => {
                dispatch({ type: 'removeChip', tab, index });
              }}
              onApplyTune={applyTune}
              onEditTalk={(value) => {
                dispatch({ type: 'setTalkTrack', value });
              }}
              onEditEmailSubject={(value) => {
                dispatch({ type: 'setEmailSubject', value });
              }}
              onEditEmailBody={(value) => {
                dispatch({ type: 'setEmailBody', value });
              }}
            />
          )}
          {state.step === 2 && (
            <PreviewStep
              state={state}
              targets={targets}
              product={product}
              onSetFormat={(format) => {
                dispatch({ type: 'setExportFormat', format });
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-border-1 flex items-center justify-between border-t px-6 py-3">
          <span className="text-text-3 text-xs">
            Step {state.step + 1} of {CC_STEPS.length}
          </span>
          <div className="flex items-center gap-2">
            {state.step > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  dispatch({ type: 'back' });
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                dispatch({ type: 'markDraftSaved' });
                showToast('Draft saved', 'success');
              }}
            >
              Save as Draft
            </Button>
            <Button
              disabled={blocked}
              onClick={handleContinue}
              title={blocked ? gatingHint(state, targets.length) : ''}
            >
              {state.step === CC_STEPS.length - 1
                ? state.exportFormat === 'csv'
                  ? 'Export CSV'
                  : 'Copy'
                : 'Continue'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border-1 rounded-md border p-2.5">
      <div className="text-text-3 text-xs">{label}</div>
      <div className="text-text-0 mt-0.5 font-medium">{value}</div>
    </div>
  );
}

// ── Step 1: Product ───────────────────────────────────────────────────────--
function ProductStep({
  product,
  products,
  candidates,
  targets,
  onSelect,
  onClear,
}: {
  product: Product | null;
  products: Product[];
  candidates: Customer[];
  targets: Customer[];
  onSelect: (id: string) => void;
  onClear: () => void;
}) {
  // Picker grid sorted by how many of the candidate pool are eligible — the
  // same shared predicate the auto-target resolution uses (eligibleTargets).
  const ranked = useMemo(() => {
    return products
      .map((p) => ({ product: p, eligible: eligibleTargets(candidates, p).length }))
      .sort((a, b) => b.eligible - a.eligible);
  }, [products, candidates]);

  if (product) {
    return (
      <div className="space-y-3">
        <button type="button" onClick={onClear} className="text-text-3 hover:text-text-1 text-xs">
          All products / <span className="text-text-1">{product.name}</span>
        </button>
        <div className="border-border-1 rounded-lg border p-4">
          <div className="flex items-center gap-2">
            <h3 className="text-text-0 text-lg font-semibold">{product.name}</h3>
            {product.coreType && (
              <span className="bg-brand-dim text-brand-primary rounded-full px-2 py-0.5 text-xs">
                {product.coreType}
              </span>
            )}
          </div>
          <p className="text-text-2 mt-1 text-sm">{product.category}</p>
          {product.description && (
            <p className="text-text-1 mt-3 text-sm leading-relaxed">{product.description}</p>
          )}
          <div className="border-border-1 mt-3 grid grid-cols-2 gap-3 border-t pt-3 text-sm">
            <Stat label="Best for" value={product.targetCustomer ?? '—'} />
            <Stat
              label="Eligibility"
              value={`${String(product.requiredMatches)} of ${String(product.rules.length)} rules`}
            />
          </div>
          {product.valueProposition && (
            <p className="text-text-1 bg-bg-3 mt-3 rounded-md px-3 py-2 text-sm">
              {product.valueProposition}
            </p>
          )}
        </div>

        <TargetPanel targets={targets} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {ranked.map(({ product: p, eligible }) => (
        <button
          key={p.id}
          type="button"
          onClick={() => {
            onSelect(p.id);
          }}
          className="border-border-1 hover:border-brand-primary rounded-lg border p-3 text-left"
        >
          <div className="text-text-0 font-medium">{p.name}</div>
          <div className="text-text-3 mt-1 text-xs">
            {eligible.toLocaleString('en-US')} of {candidates.length.toLocaleString('en-US')}{' '}
            eligible
          </div>
        </button>
      ))}
    </div>
  );
}

/**
 * The Product step's target panel (SCRUM-500): who this campaign will reach —
 * the contextual seed, or the auto-computed eligible set for the selected
 * product. Count + reduced snapshot stats + a reviewable scrollable list, so
 * officers always see the target set before writing messaging.
 */
function TargetPanel({ targets }: { targets: Customer[] }) {
  const snap = targetSnapshot(targets);
  return (
    <div className="border-border-1 rounded-lg border p-4" data-testid="wizard-target-panel">
      <div className="flex items-baseline justify-between">
        <h4 className="text-text-3 font-mono text-[0.625rem] font-semibold tracking-wide uppercase">
          Target set
        </h4>
        <span className="text-text-0 text-lg font-semibold">
          {snap.count.toLocaleString('en-US')} customer{snap.count === 1 ? '' : 's'}
        </span>
      </div>
      {snap.count === 0 ? (
        <p className="text-text-3 mt-2 text-sm">
          No eligible customers for this product — pick a different product to continue.
        </p>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
            <Stat label="Avg balance" value={`$${snap.avgBalance.toLocaleString('en-US')}`} />
            <Stat label="Avg tenure" value={`${String(snap.avgTenureMonths)} months`} />
            <Stat
              label="Account types"
              value={`${String(snap.personal)} Personal · ${String(snap.business)} Business`}
            />
          </div>
          <ul className="border-border-1 mt-3 max-h-36 divide-y divide-(--border-1) overflow-y-auto rounded-md border">
            {targets.map((c) => (
              <li key={c.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                <span className="text-text-1">{c.name}</span>
                <span className="text-text-3 font-mono text-xs">
                  {formatCompactMoney(c.balance)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ── Step 2: Messaging ─────────────────────────────────────────────────────--
interface MessagingStepProps {
  state: ReturnType<typeof initCcState>;
  hasProduct: boolean;
  onSetName: (name: string) => void;
  onToggleNameEdit: () => void;
  onSetTab: (tab: 'talk' | 'email') => void;
  onRegenerate: (tab: 'talk' | 'email') => void;
  onAddChip: (tab: 'talk' | 'email', chip: string) => void;
  onRemoveChip: (tab: 'talk' | 'email', index: number) => void;
  onApplyTune: (tab: 'talk' | 'email', freeform: string) => void;
  // SCRUM-994: hand-editing of the generated messaging fields.
  onEditTalk: (value: string) => void;
  onEditEmailSubject: (value: string) => void;
  onEditEmailBody: (value: string) => void;
}

function MessagingStep({
  state,
  onSetName,
  onToggleNameEdit,
  onSetTab,
  onRegenerate,
  onAddChip,
  onRemoveChip,
  onApplyTune,
  onEditTalk,
  onEditEmailSubject,
  onEditEmailBody,
}: MessagingStepProps) {
  const tab = state.previewTab;
  const chips = state.tuneChips[tab];
  const generating = state.generating[tab];

  return (
    <div className="space-y-4">
      {/* Campaign name */}
      <div>
        <div className="text-text-3 mb-1 block text-xs">Campaign name</div>
        <div className="flex items-center gap-2">
          {state.nameEditing ? (
            <Input
              value={state.campaignName}
              onChange={(e) => {
                onSetName(e.target.value);
              }}
              className="flex-1"
            />
          ) : (
            <span className="text-text-0 flex-1 font-medium">{state.campaignName}</span>
          )}
          <button
            type="button"
            onClick={onToggleNameEdit}
            className="text-text-3 hover:text-text-1"
          >
            {state.nameEditing ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Channel tabs */}
      <div className="border-border-1 flex gap-1 border-b">
        {(['talk', 'email'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              onSetTab(t);
            }}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm ${
              tab === t
                ? 'border-brand-primary text-text-0'
                : 'text-text-3 hover:text-text-1 border-transparent'
            }`}
          >
            {t === 'talk' ? 'Talk Track' : 'Email'}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <span className="bg-brand-dim text-brand-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs">
          <Sparkles className="h-3 w-3" />
          {generating ? 'Generating…' : 'AI-generated'}
        </span>
        <Button
          variant="outline"
          className="ml-auto gap-1.5"
          onClick={() => {
            onRegenerate(tab);
          }}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Regenerate
        </Button>
      </div>

      {/* Tune presets */}
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip, i) => (
          <span
            key={chip}
            className="bg-brand-dim text-brand-primary inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
          >
            {chip}
            <button
              type="button"
              onClick={() => {
                onRemoveChip(tab, i);
              }}
            >
              ×
            </button>
          </span>
        ))}
        {TUNE_PRESETS.filter((p) => !chips.includes(p)).map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => {
              onAddChip(tab, preset);
            }}
            className="border-border-1 text-text-2 hover:bg-bg-3 rounded-full border px-2 py-0.5 text-xs"
          >
            + {preset}
          </button>
        ))}
        {chips.length > 0 && (
          <Button
            onClick={() => {
              onApplyTune(tab, '');
            }}
          >
            Apply
          </Button>
        )}
      </div>

      {/* Editor (editable — SCRUM-994: human-in-the-loop, officer adjusts before Preview) */}
      {tab === 'talk' ? (
        <Textarea
          aria-label="Talk track"
          value={state.talkTrack}
          onChange={(e) => {
            onEditTalk(e.target.value);
          }}
          className="bg-bg-3 min-h-48 font-sans text-sm"
        />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-text-3 shrink-0 text-xs">Subject:</span>
            <Input
              aria-label="Email subject"
              value={state.emailSubject}
              onChange={(e) => {
                onEditEmailSubject(e.target.value);
              }}
              className="bg-bg-3 flex-1"
            />
          </div>
          <Textarea
            aria-label="Email body"
            value={state.emailBody}
            onChange={(e) => {
              onEditEmailBody(e.target.value);
            }}
            className="bg-bg-3 min-h-40 font-sans text-sm"
          />
        </div>
      )}
    </div>
  );
}

// ── Step 3: Preview ───────────────────────────────────────────────────────--
function PreviewStep({
  state,
  targets,
  product,
  onSetFormat,
}: {
  state: ReturnType<typeof initCcState>;
  targets: Customer[];
  product: Product | null;
  onSetFormat: (format: 'csv' | 'copy') => void;
}) {
  const totalBalance = targets.reduce((s, c) => s + c.balance, 0);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-6">
        <Hero label="Customers" value={targets.length.toLocaleString('en-US')} />
        <Hero label="Total balance" value={formatCompactMoney(totalBalance)} />
        <Hero label="Product" value={product?.name ?? '—'} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Stat label="Campaign" value={state.campaignName} />
        <Stat label="Avg tenure" value={tenureLabel(targetSnapshot(targets).avgTenureMonths)} />
      </div>
      <div>
        <h4 className="text-text-3 mb-2 font-mono text-[0.625rem] font-semibold tracking-wide uppercase">
          {state.previewTab === 'talk' ? 'Talk track' : 'Email'} preview
        </h4>
        <pre className="border-border-1 bg-bg-3 max-h-48 overflow-auto rounded-md border p-3 font-sans text-sm whitespace-pre-wrap">
          {state.previewTab === 'talk'
            ? state.talkTrack
            : `${state.emailSubject}\n\n${state.emailBody}`}
        </pre>
      </div>

      {/* Output format */}
      <div>
        <h4 className="text-text-3 mb-2 font-mono text-[0.625rem] font-semibold tracking-wide uppercase">
          Output format
        </h4>
        <div className="border-border-1 inline-flex overflow-hidden rounded-md border">
          {(['csv', 'copy'] as const).map((fmt) => (
            <button
              key={fmt}
              type="button"
              onClick={() => {
                onSetFormat(fmt);
              }}
              className={`px-3 py-1.5 text-sm ${
                state.exportFormat === fmt
                  ? 'bg-brand-primary text-white'
                  : 'text-text-2 hover:bg-bg-3'
              }`}
            >
              {fmt === 'csv'
                ? 'CSV download'
                : `Copy ${state.previewTab === 'talk' ? 'talk track' : 'email'}`}
            </button>
          ))}
        </div>
        {state.exportFormat === 'csv' && (
          <div className="text-text-3 mt-2 font-mono text-xs">
            CSV UTF-8 · Rows {targets.length.toLocaleString('en-US')} · Columns 19 · Encoding UTF-8
            · LF · Schema CRM-ready
          </div>
        )}
      </div>
    </div>
  );
}

function Hero({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-text-3 text-xs">{label}</div>
      <div className="text-text-0 text-lg font-semibold">{value}</div>
    </div>
  );
}
